import { useMemo } from "react";
import type { UrlConfigurationResult } from "./useUrlConfiguration";
import { useSessionRestoreValidation } from "./useSessionRestoreValidation";
import type { ComponentSelections } from "../utils/componentSelections";
import { mapBatchResultsToSelections, loadComponentSelections } from "../utils/componentSelections";

interface UseInitialSelectionsOptions {
	configBundleId: string;
	urlConfig: UrlConfigurationResult;
}

interface UseInitialSelectionsResult {
	initialSelections: ComponentSelections | null;
	isLoading: boolean;
	allowStoredSelectionsFallback: boolean;
	replacedProductGroupIds: string[];
	replacedGroupIdSet: Set<string>;
}

export function useInitialSelections({
	configBundleId,
	urlConfig,
}: UseInitialSelectionsOptions): UseInitialSelectionsResult {
	const sessionRestoreValidation = useSessionRestoreValidation({
		configBundleId,
		hasUrlParam: urlConfig.hasUrlParam,
		isUrlLoading: urlConfig.isLoading,
		isUrlInvalidConfig: urlConfig.isInvalidConfig,
	});

	const hasStoredProducts = useMemo(() => {
		if (!configBundleId) {
			return false;
		}

		const stored = loadComponentSelections(configBundleId);
		return Object.values(stored).some((selection) => !!selection.productId);
	}, [configBundleId]);

	const shouldAttemptSessionRestore = useMemo(() => {
		return (
			!!configBundleId &&
			!urlConfig.hasUrlParam &&
			!urlConfig.isLoading &&
			!urlConfig.isInvalidConfig &&
			hasStoredProducts
		);
	}, [configBundleId, urlConfig.hasUrlParam, urlConfig.isLoading, urlConfig.isInvalidConfig, hasStoredProducts]);

	const sessionResolvedSelections = useMemo(() => {
		if (urlConfig.hasUrlParam || !sessionRestoreValidation.data?.selections) {
			return null;
		}

		return mapBatchResultsToSelections(sessionRestoreValidation.data.selections);
	}, [urlConfig.hasUrlParam, sessionRestoreValidation.data?.selections]);

	const initialSelections = useMemo(() => {
		if (urlConfig.componentSelections) {
			return urlConfig.componentSelections;
		}

		return sessionResolvedSelections;
	}, [urlConfig.componentSelections, sessionResolvedSelections]);

	const isLoading = useMemo(() => {
		if (!shouldAttemptSessionRestore) {
			return false;
		}

		if (sessionRestoreValidation.isError) {
			return false;
		}

		return !sessionRestoreValidation.data;
	}, [shouldAttemptSessionRestore, sessionRestoreValidation.data, sessionRestoreValidation.isError]);

	const allowStoredSelectionsFallback = useMemo(() => {
		return !shouldAttemptSessionRestore;
	}, [shouldAttemptSessionRestore]);

	const replacedProductGroupIds = useMemo(() => {
		if (urlConfig.hasUrlParam) {
			return urlConfig.replacedProductGroupIds;
		}

		return sessionRestoreValidation.data?.replacedProductGroupIds ?? [];
	}, [
		urlConfig.hasUrlParam,
		urlConfig.replacedProductGroupIds,
		sessionRestoreValidation.data?.replacedProductGroupIds,
	]);

	const replacedGroupIdSet = useMemo(() => {
		return new Set(replacedProductGroupIds.map((id) => id.trim().toLowerCase()));
	}, [replacedProductGroupIds]);

	return {
		initialSelections,
		isLoading,
		allowStoredSelectionsFallback,
		replacedProductGroupIds,
		replacedGroupIdSet,
	};
}
